import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiRequest } from '@/lib/api'
import type { BulkUploadResponse, Organization, Worker, WorkersListResponse } from './types'

const workersKey = (params: {
  organizationId?: string
  search?: string
  limit?: number
  offset?: number
  includeExpedienteSummary?: boolean
}) => ['workers', params] as const

export function useWorkers(
  params: {
    organizationId?: string
    search?: string
    limit?: number
    offset?: number
    /**
     * Cuando true, pide `?include=expediente_summary` y cada Worker viene con
     * `expedienteSummary: ExpedienteSummaryCompact | null` embebido.
     * Caso de uso: tabla Expediente con columnas-card (F7.6).
     */
    includeExpedienteSummary?: boolean
  } = {},
) {
  return useQuery({
    queryKey: workersKey(params),
    // PHI-sensitive (incluye fechas examen + color semáforo): staleTime corto.
    // Coincide con regla rule 04 "React Query cache: staleTime ≤60s".
    staleTime: params.includeExpedienteSummary ? 60_000 : undefined,
    queryFn: async () => {
      const qs = new URLSearchParams()
      if (params.organizationId) qs.set('organizationId', params.organizationId)
      if (params.search) qs.set('search', params.search)
      qs.set('limit', String(params.limit ?? 50))
      qs.set('offset', String(params.offset ?? 0))
      if (params.includeExpedienteSummary) qs.set('include', 'expediente_summary')
      return api.get<WorkersListResponse>(`/api/workers?${qs.toString()}`)
    },
  })
}

export function useOrganizations() {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.get<{ items: Organization[] }>('/api/organizations'),
  })
}

export function useCreateWorker() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (
      payload: Partial<Worker> & {
        rut: string
        givenName: string
        familyName: string
        organizationId: string
      },
    ) => api.post<Worker>('/api/workers', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workers'] })
    },
  })
}

export function useUpdateWorker() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Worker> }) =>
      api.patch<Worker>(`/api/workers/${encodeURIComponent(id)}`, patch),
    // Optimistic update
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['workers'] })
      const snapshots = qc.getQueriesData<WorkersListResponse>({ queryKey: ['workers'] })
      for (const [key, data] of snapshots) {
        if (!data) continue
        qc.setQueryData<WorkersListResponse>(key, {
          ...data,
          items: data.items.map((w) => (w.id === id ? { ...w, ...patch } : w)),
        })
      }
      return { snapshots }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['workers'] })
    },
  })
}

export function useBulkUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, organizationId }: { file: File; organizationId: string }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('organizationId', organizationId)
      return apiRequest<BulkUploadResponse>('/api/workers/bulk-upload', {
        method: 'POST',
        rawBody: form,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workers'] })
    },
  })
}
